import {
	Autorenew, CheckCircle, Close, Download, ErrorOutline, EventBusy, FilterAlt, History, TrendingUp
} from "@mui/icons-material";
import { Button, MenuItem, Tab, Tabs, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import EpiAvatar from "../../components/epi/EpiAvatar";
import EpiStatCard from "../../components/epi/EpiStatCard";
import { EpiStatusChip } from "../../components/epi/EpiStatusChip";
import {
	EPI_COLORS, actionStatusColors, actionStatusLabels, companyLabel, periodLabel, reasonLabel, reasonLabels
} from "../../components/epi/epiColors";
import { EpiComplianceService } from "../../services/EpiComplianceService";
import { EpiCorrectiveActionService } from "../../services/EpiCorrectiveActionService";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import {
	EpiActionHistoryDTO, EpiCorrectiveActionDTO, EpiResponsibleDTO
} from "../../types/EpiCorrectiveActionDTO";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { EpiPdfService, loadCompanyLogo } from "../../utils/EpiPdfService";
import { useUtils } from "../../utils/utilFunctions";
import { IMMEDIATE_ACTIONS } from "../epi-inspection-wizard/steps/EpiChecklistStep";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiNonConformity.scss';

dayjs.extend(customParseFormat);

const tabs = ['TODAS', 'ABERTAS', 'EM_ANDAMENTO', 'VENCIDAS', 'CONCLUIDAS'];
const tabLabels = ['Todas', 'Abertas', 'Em andamento', 'Vencidas', 'Concluídas'];

const EpiNonConformity: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [companyId, setCompanyId] = useState<number | ''>('');
	const [actions, setActions] = useState<EpiCorrectiveActionDTO[]>([]);
	const [responsibles, setResponsibles] = useState<EpiResponsibleDTO[]>([]);
	const [selected, setSelected] = useState<EpiCorrectiveActionDTO>();
	const [history, setHistory] = useState<EpiActionHistoryDTO[]>([]);
	const [showHistory, setShowHistory] = useState(false);
	const [evidence, setEvidence] = useState<string>();
	const [tab, setTab] = useState(0);
	const [sector, setSector] = useState('');
	const [reason, setReason] = useState('');
	const [responsible, setResponsible] = useState('');
	const [search, setSearch] = useState('');
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [fromValue, setFromValue] = useState<Dayjs | null>(null);
	const [toValue, setToValue] = useState<Dayjs | null>(null);
	const [fromError, setFromError] = useState(false);
	const [toError, setToError] = useState(false);
	const [form, setForm] = useState({ status: '', responsibleUsername: '', dueDate: '',
		correctiveAction: '', notes: '' });
	const [dueDateValue, setDueDateValue] = useState<Dayjs | null>(null);
	const [dueDateError, setDueDateError] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const requestedCompany = useRef<number | ''>('');
	const requestedAction = useRef<number>();
	const evidenceUrl = useRef<string>();
	const { showAlert, showError } = useAlert();
	const { verifyPermission } = useUtils();
	const service = new EpiCorrectiveActionService();
	const fileService = new EpiComplianceService();
	const configService = new EpiInspectionService();
	const canResolve = permissions.includes('RESOLVE_EPI_ACTION');
	const canEditSelected = canResolve && selected?.status !== 'CONCLUIDA';
	const canExport = permissions.includes('EXPORT_EPI_REPORT');

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await configService.getCompanies();
			const enabled = (response?.data || []).filter(company => company.enabled);
			setCompanies(enabled);
			if (enabled.length === 1) {
				setCompanyId(enabled[0].companyId);
			}
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_NONCONFORMITY');
		}
	}, []);

	useEffect(() => {
		load();
	}, [companyId]);

	const periodActions = actions.filter(action => isInPeriod(action.inspectionDate));
	const invalidPeriod = isInvalidPeriod();
	const filtered = filter();
	const open = periodActions.filter(action => action.status !== 'CONCLUIDA');
	const overdue = periodActions.filter(action => action.overdue);
	const replacements = open.filter(action => action.generatesReplacement);
	const concluded = periodActions.filter(action => action.status === 'CONCLUIDA');
	const sectors = distinct(actions.map(action => action.sectorName));

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-nonconformity">
				<div className="cabecalho">
					<Typography variant="h6">Não Conformidades e Plano de Ação</Typography>
					<span>Gerencie as não conformidades identificadas e as ações corretivas.</span>
				</div>
				<div className="indicadores">
					<EpiStatCard
						icon={<ErrorOutline />}
						color={EPI_COLORS.danger}
						value={String(open.length)}
						label="Não conformidades não concluídas"
					/>
					<EpiStatCard
						icon={<EventBusy />}
						color={EPI_COLORS.orange}
						value={String(overdue.length)}
						label="Vencidas (atrasadas)"
					/>
					<EpiStatCard
						icon={<Autorenew />}
						color={EPI_COLORS.info}
						value={String(replacements.length)}
						label="Substituições pendentes"
					/>
					<EpiStatCard
						icon={<CheckCircle />}
						color={EPI_COLORS.success}
						value={String(concluded.length)}
						label="Ações concluídas"
					/>
					<EpiStatCard
						icon={<TrendingUp />}
						color={EPI_COLORS.purple}
						value={periodActions.length ? `${(concluded.length * 100 / periodActions.length).toFixed(1)}%` : '—'}
						label="Índice de resolução"
					/>
				</div>
				<div className="filtros">
					<TextField
						select
						label="Empresa"
						size="small"
						value={companyId}
						onChange={(e) => setCompanyId(Number(e.target.value))}
					>
						{companies.map(company => (
							<MenuItem key={company.companyId} value={company.companyId}>
								{companyLabel(company)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Setor"
						size="small"
						value={sector}
						onChange={(e) => setSector(e.target.value)}
					>
						<MenuItem value="">Todos os setores</MenuItem>
						{sectors.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Situação"
						size="small"
						value={tab === 0 ? '' : tab}
						onChange={(e) => setTab(Number(e.target.value))}
					>
						<MenuItem value="">Todas as situações</MenuItem>
						{tabLabels.slice(1).map((label, index) => (
							<MenuItem key={label} value={index + 1}>{label}</MenuItem>
						))}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Tipo de problema"
						size="small"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					>
						<MenuItem value="">Todos</MenuItem>
						{Object.keys(reasonLabels).map(key => (
							<MenuItem key={key} value={key}>{reasonLabels[key]}</MenuItem>
						))}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Responsável"
						size="small"
						value={responsible}
						onChange={(e) => setResponsible(e.target.value)}
					>
						<MenuItem value="">Todos</MenuItem>
						{responsibles.map(item => (
							<MenuItem key={item.username} value={item.username}>{item.name}</MenuItem>
						))}
					</TextField>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
						<DatePicker
							label="De"
							value={fromValue}
							onChange={(newValue) => handleFromChange(newValue)}
							slotProps={{ textField: { size: 'small', error: fromError, helperText: fromError ? 'Data inválida' : undefined } }}
						/>
						<DatePicker
							label="Até"
							value={toValue}
							onChange={(newValue) => handleToChange(newValue)}
							slotProps={{ textField: { size: 'small', error: toError, helperText: toError ? 'Data inválida' : undefined } }}
						/>
					</LocalizationProvider>
					<TextField
						label="Buscar colaborador, EPI ou descrição"
						size="small"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<div className="acoes-filtro">
						<Button variant="outlined" startIcon={<FilterAlt />} onClick={clearFilters}>
							Limpar filtros
						</Button>
					</div>
				</div>
				<div className="abas">
					<Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
						<Tab label={`Todas (${periodActions.length})`} />
						<Tab label={`Abertas (${periodActions.filter(a => a.status === 'ABERTA').length})`} />
						<Tab label={`Em andamento (${periodActions.filter(a => a.status === 'EM_ANDAMENTO').length})`} />
						<Tab label={`Vencidas (${overdue.length})`} />
						<Tab label={`Concluídas (${concluded.length})`} />
					</Tabs>
					{canExport && (
						<Button
							variant="outlined"
							startIcon={<Download />}
							disabled={loading || exporting || !filtered.length}
							onClick={exportReport}
						>
							Exportar relatório
						</Button>
					)}
				</div>
				{loading
					? getLoading()
					: <div className="corpo">
						<div className="tabela">
							<div className="header-tabela">
								<p>Data</p>
								<p>Colaborador</p>
								<p>EPI / Problema</p>
								<p>Responsável</p>
								<p>Prazo</p>
								<p>Situação</p>
							</div>
							{!filtered.length
								? <div className="sem-execucoes">
									<Typography variant="body1">
										{companyId === ''
											? 'Selecione uma empresa para ver as não conformidades.'
											: invalidPeriod
												? 'A data "De" é posterior à data "Até".'
												: `Nenhuma não conformidade encontrada${periodText()}.`}
									</Typography>
								</div>
								: filtered.map(action => (
									<div
										className={selected?.id === action.id ? 'linha selecionada' : 'linha'}
										key={action.id}
										onClick={() => select(action)}
									>
										<p>{action.inspectionDate || '—'}</p>
										<p className="colaborador">
											<EpiAvatar name={action.employeeName} color={EPI_COLORS.info} />
											<span>
												{action.employeeName}
												<small>{action.employeeRegistration || '—'}</small>
											</span>
										</p>
										<p>
											{action.epiName}
											<small>{reasonLabel(action.nonConformityReason, action.nonConformityReasonDetail)}</small>
										</p>
										<p>{action.responsibleName || '—'}</p>
										<p style={{ color: action.overdue ? EPI_COLORS.danger : undefined }}>
											{action.dueDate || '—'}
											{action.daysToDueDate !== undefined && action.daysToDueDate !== null
												? ` (${action.daysToDueDate} dias)`
												: ''}
										</p>
										<p>
											<EpiStatusChip
												label={action.overdue
													? 'Vencida'
													: actionStatusLabels[action.status] || action.status}
												color={action.overdue
													? EPI_COLORS.danger
													: actionStatusColors[action.status] || EPI_COLORS.neutral}
											/>
										</p>
									</div>
								))
							}
						</div>
						{!!selected && (
							<div className="painel-detalhe">
								<div className="topo-painel">
									<Typography variant="subtitle1">Detalhes da não conformidade</Typography>
									<Close onClick={() => setSelected(undefined)} />
								</div>
								<div className="identificacao">
									<Typography variant="h6">{selected.epiName}</Typography>
									<EpiStatusChip
										label={selected.overdue
											? 'Vencida'
											: actionStatusLabels[selected.status] || selected.status}
										color={selected.overdue
											? EPI_COLORS.danger
											: actionStatusColors[selected.status] || EPI_COLORS.neutral}
										filled
									/>
									<span>Inspeção nº {selected.inspectionNumber} · {selected.inspectionDate}</span>
								</div>
								<div className="dados">
									<span><strong>Colaborador: </strong>{selected.employeeName}</span>
									<span><strong>Matrícula: </strong>{selected.employeeRegistration || '—'}</span>
									<span><strong>Setor: </strong>{selected.sectorName || '—'}</span>
									<span><strong>Cargo: </strong>{selected.positionName || '—'}</span>
									<span><strong>CA: </strong>{selected.ca || '—'}</span>
									<span><strong>Motivo: </strong>
										{reasonLabel(selected.nonConformityReason, selected.nonConformityReasonDetail)}</span>
								</div>
								<div className="bloco">
									<Typography variant="subtitle2">Descrição do problema</Typography>
									<p>{selected.problemDescription || 'Sem descrição registrada.'}</p>
									{!!evidence && <img src={evidence} alt="Evidência da não conformidade" />}
								</div>
								<div className="bloco">
									<Typography variant="subtitle2">Ação imediata realizada</Typography>
									<p>{(selected.immediateAction && (IMMEDIATE_ACTIONS[selected.immediateAction] || selected.immediateAction))
										|| 'Nenhuma ação imediata registrada.'}</p>
								</div>
								{canEditSelected
									? <Fragment>
										<TextField
											select
											label="Situação da ação"
											size="small"
											value={form.status}
											onChange={(e) => setForm(previous => ({ ...previous, status: e.target.value }))}
										>
											{Object.keys(actionStatusLabels).map(key => (
												<MenuItem key={key} value={key}>{actionStatusLabels[key]}</MenuItem>
											))}
										</TextField>
										<TextField
											select
											SelectProps={{ displayEmpty: true }}
											InputLabelProps={{ shrink: true }}
											label="Responsável"
											size="small"
											value={form.responsibleUsername}
											onChange={(e) => setForm(previous =>
												({ ...previous, responsibleUsername: e.target.value }))}
										>
											<MenuItem value="">Sem responsável</MenuItem>
											{responsibleOptions().map(item => (
												<MenuItem key={item.username} value={item.username}>{item.name}</MenuItem>
											))}
										</TextField>
										<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
											<DatePicker
												label="Prazo"
												value={dueDateValue}
												onChange={(newValue) => handleDueDateChange(newValue)}
												slotProps={{ textField: { size: 'small', error: dueDateError, helperText: dueDateError ? 'Data inválida' : undefined } }}
											/>
										</LocalizationProvider>
										<TextField
											label="Ação corretiva definida"
											size="small"
											multiline
											minRows={2}
											value={form.correctiveAction}
											onChange={(e) => setForm(previous =>
												({ ...previous, correctiveAction: e.target.value }))}
										/>
										<TextField
											label="Observações"
											size="small"
											multiline
											minRows={2}
											value={form.notes}
											onChange={(e) => setForm(previous => ({ ...previous, notes: e.target.value }))}
										/>
										<div className="acoes-painel">
											<Button variant="outlined" startIcon={<History />} onClick={loadHistory}>
												Histórico
											</Button>
											<Button variant="contained" disabled={saving} onClick={save}>
												Salvar alterações
											</Button>
										</div>
									</Fragment>
									: <Fragment>
										<div className="bloco">
											<Typography variant="subtitle2">Ação corretiva definida</Typography>
											<p>{selected.correctiveAction || 'Ainda não definida.'}</p>
										</div>
										<div className="bloco">
											<Typography variant="subtitle2">Responsável e prazo</Typography>
											<p>{selected.responsibleName || '—'} · {selected.dueDate || 'sem prazo'}</p>
										</div>
										<div className="acoes-painel">
											<Button variant="outlined" startIcon={<History />} onClick={loadHistory}>
												Histórico
											</Button>
										</div>
									</Fragment>
								}
								{showHistory && (
									<div className="bloco historico">
										<Typography variant="subtitle2">Histórico</Typography>
										{!history.length
											? <p>Nenhuma alteração registrada.</p>
											: history.map((entry, index) => (
												<div className="entrada" key={index}>
													<strong>{entry.description}</strong>
													<span>{entry.createdAt} · {entry.name || '—'}</span>
												</div>
											))
										}
									</div>
								)}
							</div>
						)}
					</div>
				}
			</div>
		</Fragment>
	);

	async function load() {
		if (companyId === '') {
			setActions([]);
			setResponsibles([]);
			return;
		}
		const requested = companyId;
		requestedCompany.current = requested;
		setLoading(true);
		const response = await service.list(Number(requested));
		if (requestedCompany.current !== requested) {
			return;
		}
		setActions(response?.data || []);
		const responsibleResponse = await service.listResponsibles(Number(requested));
		if (requestedCompany.current !== requested) {
			return;
		}
		setResponsibles(responsibleResponse?.data || []);
		setSelected(undefined);
		setLoading(false);
	}

	async function select(action: EpiCorrectiveActionDTO) {
		requestedAction.current = action.id;
		setSelected(action);
		setShowHistory(false);
		setHistory([]);
		if (evidenceUrl.current) {
			URL.revokeObjectURL(evidenceUrl.current);
			evidenceUrl.current = undefined;
		}
		setEvidence(undefined);
		setDueDateValue(action.dueDate ? dayjs(action.dueDate, 'DD/MM/YYYY') : null);
		setDueDateError(false);
		setForm({
			status: action.status,
			responsibleUsername: action.responsibleUsername || '',
			dueDate: action.dueDate || '',
			correctiveAction: action.correctiveAction || '',
			notes: action.notes || ''
		});
		const firstEvidence = (action.evidenceFiles || '').split(',')
			.map(name => name.trim())
			.filter(name => !!name)[0];
		if (firstEvidence) {
			const response = await fileService.getFile(action.inspectionId, firstEvidence);
			if (requestedAction.current !== action.id) {
				return;
			}
			if (response?.data) {
				const url = URL.createObjectURL(response.data as Blob);
				evidenceUrl.current = url;
				setEvidence(url);
			}
		}
	}

	async function loadHistory() {
		if (!selected) {
			return;
		}
		const response = await service.history(selected.id);
		setHistory(response?.data || []);
		setShowHistory(true);
	}

	function handleDueDateChange(newValue: Dayjs | null) {
		setDueDateValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setDueDateError(true);
			return;
		}
		setDueDateError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		setForm(previous => ({ ...previous, dueDate: value }));
	}

	async function save() {
		if (!selected) {
			return;
		}
		if (dueDateError) {
			showError('Prazo inválido.');
			return;
		}
		setSaving(true);
		try {
			const chosen = responsibleOptions()
				.find(item => item.username === form.responsibleUsername);
			const response = await service.update(selected.id, {
				status: form.status,
				responsibleUsername: form.responsibleUsername || undefined,
				responsibleName: chosen?.name,
				dueDate: form.dueDate || undefined,
				correctiveAction: form.correctiveAction,
				notes: form.notes
			});
			setActions(previous => previous
				.map(action => action.id === response.data.id ? response.data : action));
			setSelected(response.data);
			showAlert('Ação corretiva atualizada.');
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível salvar a ação corretiva.');
		}
		setSaving(false);
	}

	function responsibleOptions() {
		if (!selected?.responsibleUsername) {
			return responsibles;
		}
		return responsibles.some(item => item.username === selected.responsibleUsername)
			? responsibles
			: responsibles.concat([{
				username: selected.responsibleUsername,
				name: selected.responsibleName || selected.responsibleUsername
			}]);
	}

	async function exportReport() {
		setExporting(true);
		try {
			new EpiPdfService().exportNonConformitiesToPdf(filtered, {
				companyName: companies.find(item => item.companyId === companyId)?.companyName || '',
				situation: tabLabels[tab],
				period: periodLabel(from, to) || 'Todas as datas',
				sector: sector || 'Todos os setores',
				reason: reason ? reasonLabels[reason] : 'Todos',
				responsible: responsible
					? responsibles.find(item => item.username === responsible)?.name || responsible
					: 'Todos',
				search: search.trim()
			}, await loadCompanyLogo(companyId));
		} catch {
			showError('Não foi possível gerar o relatório.');
		}
		setExporting(false);
	}

	function clearFilters() {
		setTab(0);
		setSector('');
		setReason('');
		setResponsible('');
		setSearch('');
		setFrom('');
		setTo('');
		setFromValue(null);
		setToValue(null);
		setFromError(false);
		setToError(false);
	}

	function handleFromChange(newValue: Dayjs | null) {
		setFromValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setFromError(true);
			return;
		}
		setFromError(false);
		setFrom(newValue === null ? '' : newValue.format('DD/MM/YYYY'));
	}

	function handleToChange(newValue: Dayjs | null) {
		setToValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setToError(true);
			return;
		}
		setToError(false);
		setTo(newValue === null ? '' : newValue.format('DD/MM/YYYY'));
	}

	function isInPeriod(value?: string) {
		if (!from && !to) {
			return true;
		}
		const date = dayjs(value || '', 'DD/MM/YYYY', true);
		if (!date.isValid()) {
			return false;
		}
		return (!from || !date.isBefore(dayjs(from, 'DD/MM/YYYY'), 'day'))
			&& (!to || !date.isAfter(dayjs(to, 'DD/MM/YYYY'), 'day'));
	}

	function isInvalidPeriod() {
		return !!from && !!to && dayjs(from, 'DD/MM/YYYY').isAfter(dayjs(to, 'DD/MM/YYYY'), 'day');
	}

	function periodText() {
		const label = periodLabel(from, to);
		return label ? ` ${label}` : '';
	}

	function distinct(values: string[]) {
		return Array.from(new Set(values.filter(value => !!value))).sort();
	}

	function filter() {
		return periodActions.filter(action => {
			const current = tabs[tab];
			if (current === 'ABERTAS' && action.status !== 'ABERTA') {
				return false;
			}
			if (current === 'EM_ANDAMENTO' && action.status !== 'EM_ANDAMENTO') {
				return false;
			}
			if (current === 'VENCIDAS' && !action.overdue) {
				return false;
			}
			if (current === 'CONCLUIDAS' && action.status !== 'CONCLUIDA') {
				return false;
			}
			if (sector && action.sectorName !== sector) {
				return false;
			}
			if (reason && action.nonConformityReason !== reason) {
				return false;
			}
			if (responsible && action.responsibleUsername !== responsible) {
				return false;
			}
			if (!search) {
				return true;
			}
			const term = search.toUpperCase();
			return (action.employeeName || '').toUpperCase().includes(term)
				|| (action.epiName || '').toUpperCase().includes(term)
				|| (action.problemDescription || '').toUpperCase().includes(term);
		});
	}
};

export default EpiNonConformity;
